import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import type { ChallengeInfo } from "@/api/types";
import { AuthScreen, PrimaryButton, GhostButton, Spinner, ErrorNote } from "@/components/auth/ui";

/** Consent screen for non-first-party relying parties. */
export function ConsentPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const challenge = params.get("challenge");
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, status } = useQuery<ChallengeInfo>({
    queryKey: ["challenge", challenge],
    queryFn: () => api.get<ChallengeInfo>(`/oidc/challenge/${challenge}`),
    enabled: !!challenge,
    retry: false,
  });

  async function decide(granted: boolean) {
    setBusy(granted ? "approve" : "deny");
    setError(null);
    try {
      const res = await api.post<{ handoffId: string }>("/oidc/consent", { challenge, granted });
      navigate(`/sso/redirect?handoff=${res.handoffId}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not record your choice.");
      setBusy(null);
    }
  }

  if (status === "pending") {
    return (
      <AuthScreen title="Loading…">
        <div className="flex justify-center py-4">
          <Spinner size={24} />
        </div>
      </AuthScreen>
    );
  }
  if (status === "error" || !data) {
    return (
      <AuthScreen title="Request expired" subtitle="Return to the app and try signing in again." />
    );
  }

  return (
    <AuthScreen
      title={`Authorize ${data.client.name}`}
      subtitle={
        <>
          <span className="font-semibold text-app">{data.client.name}</span> ({data.redirectHost}) wants to access:
        </>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      <ul className="mb-5 space-y-2">
        {data.scopes.map((s) => (
          <li key={s.scope} className="flex items-start gap-2.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-3 py-2.5 text-sm">
            <span className="mt-0.5 text-[hsl(var(--accent))]">✓</span>
            <span className="text-app-1">{s.description}</span>
          </li>
        ))}
      </ul>
      <div className="flex gap-3">
        <GhostButton className="flex-1" onClick={() => decide(false)} disabled={busy !== null}>
          {busy === "deny" ? <Spinner size={15} /> : "Deny"}
        </GhostButton>
        <PrimaryButton className="flex-1" onClick={() => decide(true)} loading={busy === "approve"}>
          Allow
        </PrimaryButton>
      </div>
    </AuthScreen>
  );
}
